import React, { useState } from "react";
import { Button } from "newui/components/Button";
import { useMfaStatus } from "../../hooks/useMfaStatus";
import { MFA_LOW_CODES_THRESHOLD } from "../../types/Mfa.type";
import { TotpEnrolmentWizard } from "./TotpEnrolmentWizard";
import { RegenerateCodesModal } from "./RegenerateCodesModal";
import { DisableMfaModal } from "./DisableMfaModal";
import { Spinner, Tooltip } from "@chakra-ui/react";
import { RecoveryReenrolModal } from "./RecoveryReenrolModal";

export interface MfaSettingsSectionProps {
	/**
	 * Called once 2FA is fully enabled (recovery codes acknowledged) or fully
	 * disabled — lets a host modal (e.g. `TwoFactorAuthModal`) close itself and
	 * return to the underlying screen instead of staying open on the now-settled
	 * management view (PRO-731 follow-up).
	 */
	onSettled?: () => void;
}

export const MfaSettingsSection = ({ onSettled }: MfaSettingsSectionProps) => {
	const { data: status, isLoading, refetch } = useMfaStatus();
	const [isEnrolling, setIsEnrolling] = useState(false);
	const [isRegenerateOpen, setIsRegenerateOpen] = useState(false);
	const [isReenrolOpen, setIsReenrolOpen] = useState(false);
	const [isDisableOpen, setIsDisableOpen] = useState(false);

	const handleAlreadyEnabled = () => {
		setIsEnrolling(false);
		refetch();
	};

	const handleEnrolmentComplete = () => {
		setIsEnrolling(false);
		refetch();
		onSettled?.();
	};

	if (isLoading || !status) {
		return (
			<div className="p-6" data-testid="mfa-settings-loading">
				<Spinner size="sm" /> Loading two-factor authentication settings…
			</div>
		);
	}

	if (isEnrolling) {
		return (
			<div className="max-w-lg" data-testid="totp-enrolment-wizard-panel">
				<TotpEnrolmentWizard
					onAlreadyEnabled={handleAlreadyEnabled}
					onComplete={handleEnrolmentComplete}
				/>
			</div>
		);
	}

	if (!status.two_factor_enabled) {
		return (
			<div className="flex max-w-md flex-col gap-4" data-testid="mfa-disabled-state">
				<Button
					type="button"
					btntype="primary"
					onClick={() => setIsEnrolling(true)}
					data-testid="enable-2fa-btn"
				>
					Enable two-factor authentication
				</Button>
				<p className="font-roboto text-sm text-gray-600">
					Two-factor authentication is currently disabled for your account. Enabling it adds a
					second step at login using an authenticator app.
				</p>
			</div>
		);
	}

	const isLowOnCodes = status.recovery_codes_remaining <= MFA_LOW_CODES_THRESHOLD;

	return (
		<div className="flex max-w-md flex-col gap-4 p-6" data-testid="mfa-enabled-state">
			<div className="flex items-center gap-2">
				<span
					className="rounded-full bg-blue-50 px-3 py-1 font-roboto text-sm font-semibold text-blue-700"
					data-testid="mfa-enabled-badge"
				>
					{"Authenticator app"} enabled
				</span>
			</div>
			<p className="font-roboto text-sm text-gray-600" data-testid="mfa-recovery-codes-remaining">
				{status.recovery_codes_remaining} recovery codes remaining
			</p>
			{isLowOnCodes && (
				<div
					className="rounded-md border border-yellow-300 bg-yellow-50 p-3 font-roboto text-sm text-yellow-800"
					role="alert"
					data-testid="low-recovery-codes-warning"
				>
					You&apos;re running low on recovery codes. Regenerate them to avoid losing access to
					your account.
				</div>
			)}
			<div className="flex gap-3">
				<Button
					type="button"
					btntype="secondary"
					onClick={() => setIsRegenerateOpen(true)}
					data-testid="regenerate-codes-btn"
				>
					Regenerate recovery codes
				</Button>
				<Tooltip
					label="Two-factor authentication is required by your company and cannot be disabled."
					isDisabled={!status.is_2fa_enforced}
					shouldWrapChildren
					hasArrow
				>
					<Button
						type="button"
						btntype="danger"
						onClick={() => setIsDisableOpen(true)}
						disabled={status.is_2fa_enforced}
						data-testid="disable-2fa-btn"
					>
						Disable
					</Button>
				</Tooltip>
			</div>
			<div>
				<Tooltip
					label="Re-enrolling will clear your existing recovery codes and issue a new secret for your authenticator app. Use this if you have lost access to your authenticator, or have a new device."
					shouldWrapChildren
					hasArrow
				>
					<Button
						type="button"
						btntype="secondary"
						onClick={() => setIsReenrolOpen(true)}
						data-testid="reenrol-authenticator-btn"
					>
						Re-enrol in authenticator app
					</Button>
				</Tooltip>
			</div>
			<RegenerateCodesModal
				isOpen={isRegenerateOpen}
				onClose={() => setIsRegenerateOpen(false)}
			/>
			<DisableMfaModal
				isOpen={isDisableOpen}
				onClose={() => setIsDisableOpen(false)}
				onDisabled={() => {
					setIsDisableOpen(false);
					refetch();
					onSettled?.();
				}}
			/>
			<RecoveryReenrolModal
				isOpen={isReenrolOpen}
				isUserReEnrol={true}
				onClose={() => setIsReenrolOpen(false)}
			/>
		</div>
	);
};
